import {
  createApi,
  fetchBaseQuery,
  type BaseQueryApi,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { errorCode, isTransientError } from './errors';
import { mayHaveSession } from './sessionHint';
import {
  selectAccessToken,
  sessionEnded,
  sessionStarted,
  type WithSession,
} from './sessionSlice';
import type { SessionOut } from './types';

const rawBaseQuery = fetchBaseQuery({
  // Same origin as the page: the gateway serves the app and the API together in every
  // environment, so one build works everywhere and no CORS setup is needed.
  baseUrl: new URL('/api/v1', globalThis.location.origin).href,
  credentials: 'same-origin',
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as WithSession;
    const token = selectAccessToken(state);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return headers;
  },
});

export type RefreshOutcome = 'ok' | 'signed-out' | 'unreachable';

const REFRESH_LOCK = 'easymusic-session-refresh';
const RETRY_DELAY_MS = 400;

const hasLocks = () => typeof navigator !== 'undefined' && Boolean(navigator.locks);

/** Refresh tokens are single-use, so tabs of one browser must not refresh at the same time. */
function acrossTabs<T>(task: () => Promise<T>): Promise<T> {
  if (hasLocks()) return navigator.locks.request(REFRESH_LOCK, task) as Promise<T>;
  return task();
}

async function requestRefresh(api: BaseQueryApi): Promise<RefreshOutcome> {
  if (!mayHaveSession()) {
    // Nobody ever signed in on this browser: there is no cookie to exchange.
    api.dispatch(sessionEnded());
    return 'signed-out';
  }
  // keepalive: the answer carries the next cookie, and the token just sent no longer
  // works. If the person leaves the page before the answer comes (a link to another
  // host of the installation), the browser must still take the cookie, or the session
  // is lost with the request.
  const attempt = () =>
    rawBaseQuery({ url: '/auth/refresh', method: 'POST', keepalive: true }, api, {});
  let result = await attempt();
  if (result.error?.status === 401) {
    // Somebody else may have rotated the cookie a moment ago: another tab where the
    // browser has no lock to share, or a page of another host of the installation,
    // which the lock (one per host) does not cover. The browser holds the new cookie
    // by now, or will in a moment, so one more try can succeed.
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    result = await attempt();
  }
  if (result.data) {
    api.dispatch(sessionStarted(result.data as SessionOut));
    return 'ok';
  }
  if (isTransientError(result.error)) return 'unreachable';
  api.dispatch(sessionEnded());
  return 'signed-out';
}

let refreshing: Promise<RefreshOutcome> | null = null;

/** Exchange the refresh cookie for a new access token. Concurrent callers share one request. */
export function refreshSession(api: BaseQueryApi): Promise<RefreshOutcome> {
  refreshing ??= acrossTabs(() => requestRefresh(api)).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

function isAuthRequest(args: string | FetchArgs): boolean {
  const url = typeof args === 'string' ? args : args.url;
  return url.startsWith('/auth/');
}

/** Retries a request once after renewing an expired access token. */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status === 401 && !isAuthRequest(args)) {
    if ((await refreshSession(api)) === 'ok') {
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }
  // The account was switched off, blocked or told to confirm its e-mail again while
  // signed in: the session is over, whatever the access token still says.
  if (!isAuthRequest(args) && ACCOUNT_CLOSED.has(errorCode(result.error) ?? '')) {
    api.dispatch(sessionEnded());
  }
  return result;
};

const ACCOUNT_CLOSED = new Set(['account_disabled', 'account_blocked', 'email_not_verified']);

/** The one API slice. Endpoints are added with `injectEndpoints` (see endpoints.ts). */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  // Content: what learners are served. Editor: drafts and the tree in the content editor.
  tagTypes: [
    'Progress', 'Attempt', 'Content', 'Editor', 'Accounts', 'Moderation',
    'Teaching',
    'History',
    'Submissions',
    'Help',
    'Feedback',
    'Seo',
    'Audit',
  ],
  endpoints: () => ({}),
});
